import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { postUploadPartnerMedia } from "../../../../../apis/partnerShowcase";
import Check from "../../../../../assets/icons/Check";
import CloseIcon from "../../../../../assets/icons/CloseIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import UploadIcon from "../../../../../assets/icons/UploadIcon";
import { Input } from "../../../../../components/common/Input";

const CreatePartner = ({
  onSave,
  onRemove,
  id,
  imageUrl = "",
  onCancel = () => {},
  websiteLink,
  openUploadPopUp,
  mediaIsVideo = false,
  width,
  height,
}) => {
  const { eventId } = useParams();

  const [image, setImage] = useState(imageUrl);
  const [saveButtonDisable, setSaveButtonDisable] = useState(true);
  const [uploadLoader, setUploadLoader] = useState(false);
  const [webLink, setWebLink] = useState(websiteLink);
  const [isVideo, setIsVideo] = useState(mediaIsVideo);
  const [dimension, setDimension] = useState({ width: width, height: height });

  const fileUploadRef = useRef();
  const oldValuesRef = useRef({
    imageUrl,
    webLink,
    mediaIsVideo,
    isVideo,
    dimension,
  });

  function getImageDimensionsFromFile(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
        });
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = URL.createObjectURL(file);

      // Cleanup URL object once the image has loaded
      img.onloadend = () => {
        URL.revokeObjectURL(img.src);
      };
    });
  }

  const uploadFile = async (e) => {
    if (!uploadLoader) {
      try {
        if (e.target.files[0].size > 25000000) {
          window.Toast.fire({
            icon: "error",
            title: "File size can't be bigger than 25MB",
          });
          return;
        }
        setUploadLoader(true);
        const data = await postUploadPartnerMedia(e.target.files[0], eventId);
        if (e.target.files[0].type.includes("video")) {
          setIsVideo(true);
        } else {
          setIsVideo(false);
          const metadata = await getImageDimensionsFromFile(e.target.files[0]);
          setDimension(metadata);
        }
        setImage(data);
      } catch (error) {
        console.error(error);
        window.Toast.fire({
          icon: "error",
          title: "Something went wrong",
        });
      } finally {
        setUploadLoader(false);
      }
    }
  };

  useEffect(() => {
    if (image !== imageUrl || webLink !== websiteLink)
      setSaveButtonDisable(false);
    else setSaveButtonDisable(true);
  }, [image, webLink]);

  useEffect(() => {
    setImage(imageUrl);
    oldValuesRef.current.imageUrl = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    setWebLink(websiteLink);
    oldValuesRef.current.webLink = websiteLink;
  }, [websiteLink]);

  useEffect(() => {
    setIsVideo(mediaIsVideo);
    oldValuesRef.current.isVideo = mediaIsVideo;
  }, [mediaIsVideo]);

  useEffect(() => {
    setDimension({ height, width });
    oldValuesRef.current.dimension = { height, width };
  }, [height, width]);

  useEffect(() => {
    if (openUploadPopUp) fileUploadRef.current.click();
  }, [openUploadPopUp]);

  return (
    <div className="flex gap-12 my-4">
      <div>
        <label>
          <div
            title="Upload image"
            className={` font-medium mx-auto cursor-pointer !bg-dark-gray flex gap-4 items-center rounded-xl shadow-card  px-3 py-1.5 `}
          >
            {uploadLoader ? (
              <div className="clipPathLoader mx-auto"></div>
            ) : (
              <span>
                <UploadIcon />
              </span>
            )}

            <input
              ref={fileUploadRef}
              onChange={(e) => uploadFile(e)}
              className="hidden"
              type="file"
              accept=".png,.gif,.mp4,.mov"
            />
          </div>
        </label>
      </div>
      {image ? (
        isVideo ? (
          <video
            src={"https://img2.pixnif.com/" + image}
            height={40}
            width={40}
            className="object-contain hover:scale-[5] transition-transform max-h-[40px]"
            alt="Sponsor image"
            autoPlay
            muted
          />
        ) : (
          <img
            src={"https://img2.pixnif.com/" + image}
            height={40}
            width={40}
            className="object-contain hover:scale-[5] transition-transform max-h-[40px]"
            alt="Sponsor image"
          />
        )
      ) : (
        <div className="w-[40px]"></div>
      )}
      <Input
        value={webLink}
        onChange={setWebLink}
        className="md:w-[300px]"
        placeholder="https://www.algomage.com"
      />

      <div className="flex gap-4 items-center">
        <button
          className={`font-medium text-center !bg-dark-gray flex gap-4 items-center rounded-xl shadow-card  px-3 py-1.5 w-[40px] h-[32px] ${
            saveButtonDisable
              ? "cursor-not-allowed opacity-75"
              : " cursor-pointer"
          }`}
          disabled={saveButtonDisable}
          onClick={async () => {
            setSaveButtonDisable(true);
            const metaData = {
              isVideo,
              width: dimension.width,
              height: dimension.height,
            };
            const dataPushed = await onSave(webLink, image, metaData, id);
            if (!dataPushed) {
              if (id) {
                setImage(oldValuesRef.current.imageUrl);
                setWebLink(oldValuesRef.current.webLink);
                setIsVideo(oldValuesRef.current.isVideo);
                setDimension(oldValuesRef.current.dimension);
              }
            } else {
              oldValuesRef.current = {
                imageUrl: image,
                webLink: webLink,
                isVideo,
                dimension,
              };
            }
          }}
        >
          <Check />
        </button>
        {id ? (
          <button
            className={`font-medium cursor-pointer !bg-dark-gray flex gap-4 items-center rounded-xl shadow-card  px-3 py-1.5 w-[40px] h-[32px]`}
            onClick={() => onRemove(id)}
          >
            <DeleteIcon />
          </button>
        ) : (
          <button
            className={`font-medium cursor-pointer !bg-dark-gray flex gap-4 items-center rounded-xl shadow-card  px-3 py-1.5 w-[40px] h-[32px]`}
            onClick={onCancel}
          >
            X
          </button>
        )}
      </div>
    </div>
  );
};

export default CreatePartner;
